import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Divider } from '@mui/material'
import SignatureCanvas from 'react-signature-canvas'
import { makeStyles } from '@mui/styles'
import { Formik, Form } from 'formik'

import Modal from '@/modules/base/components/modal/big/modal-big'
import { SIZES, VARIANTS } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import { ButtonIconSquare, SquareIconButton, CancelButton } from '@/modules/base/components/buttons/index'

import IconTrashCan from '@assets/images/icons/trashcan.svg'
import IconCheck from '@assets/images/icons/check.svg'
import lang from '@/lang'
import { Checkbox } from '@/modules/base/components/inputs/index'
import ThemeInterface from '@/theme-interface'
import { Signature, SignatureItem } from '@/modules/signature/types'
import clsx from 'clsx'
import { useAppSelector } from '@/redux/store'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'

const CANVAS_HEIGHT = 170
const CANVAS_WIDTH = 510

const useStyles = makeStyles((theme: ThemeInterface) => ({
  checkboxContainer: {
    marginBottom: '20px',
  },
  checkboxTitle: {
    fontSize: '12px',
    letterSpacing: '0.24px',
    color: theme.black,
    marginLeft: '10px',
  },
  row: {
    display: 'inline-flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '20px',
    width: '100%',
  },
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: 12,
    color: theme.red,
    marginTop: 10,
    '&.absolute': {
      position: 'absolute',
      bottom: '-30px',
    },
  },
  underline: {
    borderBottom: '1px dotted #50586c',
    marginBottom: '10px',
    width: '90%',
    margin: '0 auto 20px',
  },
  canvasContainer: {
    margin: '32px 0',
    width: canvasProps.width,
    background: theme.backgroundColor,
    border: `1px solid ${theme.borderColor}`,
    position: 'relative',
    '& button': {
      position: 'absolute',
      top: '10px',
      right: '10px',
    },
    '& canvas': {
      cursor: 'pointer',
  },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
  },
}), { name: 'SignatureModal' })

interface SignatureModalProps {
  isOpen: boolean,
  onDone: (signature: Partial<Signature>, applyToAll: boolean) => void,
  onClose: () => void,
  signature?: string,
}

const canvasProps = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT }
const SignatureModal = (props: SignatureModalProps) => {
  const { isOpen, onDone, onClose, signature } = props
  const styles = useStyles()
  const canvasRef = useRef(null)
  const [canvasError, setCanvasError] = useState<null | string>(null)
  const [clientConfirmationError, setClientConfirmationError] = useState(false)
  const { items, currentItemIndex } = useSignatureContext()

  const item = items[currentItemIndex]

  const signerEmail = item?.signerEmail

  const userEmail = useAppSelector(state => state.user.info.email)
  const isTaxConsultant = useAppSelector(state => state.tenant.info.isTaxConsultant)

  const isClientMode = userEmail !== signerEmail && isTaxConsultant
  const name = item?.name.trim() || signerEmail
  const titleAppendix = isClientMode ? `: ${name}` : ''

  const clearCanvas = () => {
    if (canvasRef) canvasRef.current.clear()
  }

  const isCanvasEmpty = () => {
    return canvasRef.current.isEmpty()
  }

  const getSignatureData = () => {
    if (!canvasRef.current) {
      return
    }

    const signature = canvasRef.current.toDataURL()
    return {
      signature,
    }
  }

  const handleSubmit = async(values) => {
    setClientConfirmationError(false)
    setCanvasError(null)
    if (isClientMode && !values.confirmSignedByClient) {
      setClientConfirmationError(true)
      return
    }

    if (isCanvasEmpty()) {
      setCanvasError(lang.signature.required)
      return
    }

    const signature = getSignatureData()
    onDone?.(signature, values.applyToAll)
  }

  return (
    <Modal
      disableBackdropClick
      onClose={onClose}
      open={isOpen}
      title={lang.signature.sign + titleAppendix}
      subtitle={lang.signature.subtitle}
    >
      <Formik
        initialValues={{ signature: null, applyToAll: false, confirmSignedByClient: false }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          const { applyToAll, confirmSignedByClient } = values

          useEffect(() => {
            if (canvasRef.current && signature) {
              canvasRef.current.fromDataURL(signature, { clear: false })
            }
          }, [signature])

          return (
            <Form>
              <div className={styles.canvasContainer}>
                <SquareIconButton
                  variant={VARIANTS.white}
                  size={SIZES.medium}
                  icon={IconTrashCan}
                  onClick={clearCanvas}
                />
                <>
                  <SignatureCanvas on={() => {
                    if (canvasError) setCanvasError(null)
                  }} ref={canvasRef} canvasProps={canvasProps} />
                  {canvasError && <div className={clsx(styles.error, { absolute: true })}>{canvasError}</div>}
                </>
                <div className={styles.underline}></div>
              </div>
              <div className={styles.checkboxContainer}>
                <Checkbox
                  name="applyToAll"
                  checked={applyToAll}
                  onChange={() => setFieldValue('applyToAll', !applyToAll)}
                >
                  <span className={styles.checkboxTitle}>{lang.signature.checkbox}</span>
                </Checkbox>
              </div>
              {isClientMode
                ? <div className={styles.checkboxContainer}>
                  <Checkbox
                    name="confirmSignedByClient"
                    checked={confirmSignedByClient}
                    onChange={() => setFieldValue('confirmSignedByClient', !confirmSignedByClient)}
                  >
                    <span className={styles.checkboxTitle}>{lang.signature.checkboxSignedByClient(name)}</span>
                  </Checkbox>
                  {clientConfirmationError && <div className={styles.error}>{lang.signature.confirmationRequired}</div>}
                </div>
                : null}
              <div className={styles.row}>
                <span />
                <div className={styles.buttonRow}>
                  <CancelButton type="button" label={lang.generics.abort} onClick={onClose} />
                  <ButtonIconSquare
                    type="submit"
                    label={lang.signature.use}
                    icon={IconCheck} />
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default SignatureModal
